/**
 * Copyright Mántum All Rights Reserved.
 */
import Body from './body';
import Field from '../components/common/field';
import Group from '../components/group';
import Message from '../components/common/message';
import Tab from '../components/common/internal/tab';
import debug from 'debug';
import getTranslate from '../locales';
import { sort, validator } from '@mantum/kanela';

import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';

import type {
    Detail,
    Error,
    Exclude,
    Header,
    HttpError,
    Kanela as Component,
    Locale,
    Mode,
    Response,
    Sections,
    Tabs,
    Status
} from '@mantum/kanela';
import { KEY_ERROR_STATUS } from './constant';

const logger = debug('kanela');
const render = debug('kanela:render');

type PropTypes = {
    activeTab?: number;
    columns?: number;
    env?: Record<string, unknown>;
    header?: Header;
    locale?: Locale;
    navegable?: boolean;
    message?: {
        enabled?: boolean;
    };
    mode?: Mode;
    onChange?: (name?: string, value?: unknown) => void;
    onClick?: (name?: string, value?: unknown) => void;
    onListener?: (values?: Record<string, unknown>) => void;
    options?: Sections | Tabs;
    readonly?: boolean;
    values?: Record<string, unknown>;
    version?: number;
};

export default forwardRef<Component, PropTypes>(function Kanela(props, ref) {
    const {
        activeTab = 0,
        columns = 1,
        env = {},
        locale = 'en',
        message = {},
        navegable = false,
        readonly = false
    } = props;

    // prettier-ignore
    const { 
        enabled = true
    } = message;

    const [alert, setAlert] = useState<string | Response>({
        ok: true,
        title: '',
        status: 'Error'
    });

    const [values, setValues] = useState<Record<string, unknown>>({});

    const valuesRef = useRef<Record<string, unknown>>({});
    const inputRef = useRef<Record<string, Detail<unknown>>>({});

    const getMessage = useCallback(
        (message: string) => {
            return getTranslate(message, locale);
        },
        [locale]
    );

    useEffect(() => {
        const values = props.values || {};
        logger('load new values %o', values);
        setValues(values);
    }, [props.values]);

    useImperativeHandle(
        ref,
        () => {
            return {
                clear(exclude?: Exclude) {
                    setAlert('');
                    onClear(exclude);
                },
                reset() {
                    logger('resert form');
                    setAlert('');
                },
                get() {
                    const count = validate();
                    if (count === 0) {
                        setAlert('');
                        return valuesRef.current;
                    }

                    if (enabled) {
                        setAlert({
                            ok: false,
                            title: getMessage(
                                'An error occurred while sending the form'
                            ),
                            errors: [
                                getMessage(
                                    'There were {count} errors with your submission'
                                ).replaceAll('{count}', `${count}`)
                            ],
                            status: KEY_ERROR_STATUS
                        });
                    }
                    return null;
                },
                find(name: string) {
                    if (name && name in valuesRef.current) {
                        logger('find %s', name);
                        return valuesRef.current[name];
                    }
                    return null;
                },
                refresh(names?: string[]) {
                    onRefresh(names);
                },
                focus(value?: string) {
                    onFocus(value);
                }
            };
        },
        [enabled, getMessage]
    );

    const onFocus = (value?: string) => {
        if (value) {
            if (inputRef.current) {
                const values = inputRef.current[value];
                if (values && values.focus) {
                    return values.focus(value);
                }
            }
        }
    };

    const onClear = (exclude?: Exclude) => {
        for (const [name, ref] of Object.entries(inputRef.current)) {
            if (exclude && name in exclude) {
                if (exclude[name]) {
                    logger('field excluded for cleaning "%s"', name);
                    continue;
                }
            }

            logger('clear input field "%s"', name);
            ref.clear();

            setValues((previous) => {
                if (name in previous) {
                    logger('clear iternal value "%s"', name);
                    previous[name] = '';
                }
                return previous;
            });
        }
    };

    const validate = (): number => {
        let count = 0;
        for (const [name, ref] of Object.entries(inputRef.current)) {
            if (ref.validable) {
                const result = ref.validate();

                // prettier-ignore
                logger('validate input field "%s" (validate: %s)', name, result);
                if (!result) {
                    count = count + 1;
                }
            }
        }

        return count;
    };

    const onInit = (ref: Detail<unknown>) => {
        if (!ref || !ref.name) {
            return;
        }

        logger('init field "%s"', ref.name);
        inputRef.current[ref.name] = ref;
    };

    const onChange = (name?: string, value?: unknown) => {
        if (name) {
            logger('change field "%s": %o', name, value);
            valuesRef.current[name] = value;
        }

        if (props.onChange) {
            props.onChange(name, value);
        }
    };

    const onUnmount = (name?: string) => {
        if (name) {
            logger('unmount field %s', name);
            if (name in valuesRef.current) {
                delete valuesRef.current[name];
            }

            if (name in inputRef.current) {
                delete inputRef.current[name];
            }
        }
    };

    const onRefresh = (refresh?: string[]) => {
        refresh?.forEach((name) => {
            if (name in inputRef.current) {
                const instance = inputRef.current[name];
                if (instance.refresh) {
                    logger('refreshing field %s', name);
                    instance.refresh();
                }
            }
        });
    };

    const onCloseAlert = () => {
        setAlert('');
    };

    const options = useMemo<Sections | Tabs>(() => {
        if (!props.options) {
            return;
        }

        const keys = Object.keys(env);
        if (keys.length === 0) {
            return props.options;
        }

        let options = JSON.stringify(props.options);
        keys.forEach((key) => {
            const value = `${env[key]}`;
            if (key.includes('BOOLEAN_')) {
                options = options.replaceAll('"${' + key + '}"', value);
            }
            options = options.replaceAll('${' + key + '}', value);
        });

        return JSON.parse(options);
    }, [props.options, env]);

    const onMessage = (
        title: string,
        errors?: HttpError | string[] | Error,
        success = true,
        status?: Status
    ) => {
        setAlert({
            ok: success,
            title: title,
            errors: errors,
            status: status
        });
    };

    render('[Kanela]: %o', {
        value: props.values,
        current: values
    });

    return (
        <div role='form' data-init='true'>
            {alert && typeof alert === 'object' && alert.title && (
                <Message
                    onClose={onCloseAlert}
                    status={alert.status}
                    title={alert.title}
                    messages={alert.errors}
                />
            )}

            {props.header && (
                <div className='mb-4 flex justify-between items-center'>
                    <h2 className='text-3xl cursor-default font-medium tracking-tight text-gray-900'>
                        {props.header?.title}
                    </h2>
                    <div className='flex gap-4'>
                        {props.header?.fields
                            .sort(sort.asc)
                            .map((it) => <Field key={it.name} field={it} />)}
                    </div>
                </div>
            )}

            <Group>
                {validator.isTab(props.options) ? (
                    <Tab
                        activeTab={activeTab}
                        columns={columns}
                        env={env}
                        locale={locale}
                        mode={props.mode}
                        navegable={navegable}
                        onChange={onChange}
                        onClear={onClear}
                        onClick={props.onClick}
                        onListener={props.onListener}
                        onInit={onInit}
                        onMessage={onMessage}
                        onUnmount={onUnmount}
                        readonly={readonly}
                        tabs={options as Tabs}
                        values={values}
                    />
                ) : (
                    <Body
                        columns={columns}
                        env={env}
                        mode={props.mode}
                        onChange={onChange}
                        onListener={props.onListener}
                        onClear={onClear}
                        onClick={props.onClick}
                        onInit={onInit}
                        onMessage={onMessage}
                        onUnmount={onUnmount}
                        readonly={readonly}
                        sections={options as Sections}
                        values={values}
                    />
                )}
            </Group>
        </div>
    );
});
